import clsx from "clsx";
import { useTranslation } from "react-i18next";

import type { OverrideParty } from "./helpers";

import { Typo } from "../../../Typo";
import { MASTER_COLOR_CLASS } from "./helpers";

const clampPct = (raw: string): number => {
  const digits = raw.replace(/[^\d]/g, "");
  if (digits === "") return 0;
  return Math.max(0, Math.min(100, parseInt(digits, 10)));
};

const fmtDollars = (pct: number): string =>
  `$${(Math.max(0, pct) * 10).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * The split editor for one override rule.
 *
 * Deliberately mirrors the wizard's Configure-rebate-split UI — same
 * distribution bar, same tonal input cards, same derived "You receive" card and
 * hypothetical-$1,000 caption — because a scoped split is the same decision as
 * the default split, just narrower. It carries design change #8's spacing too
 * (16px inside the split block, 8px between cards).
 *
 * `entered` tracks which inputs the user has actually typed in, separately from
 * their values: an untouched field shows the "Min 0" placeholder and the derived
 * card shows "--" rather than a misleading 100%.
 */
export const ScopedSplit = ({
  parties,
  pcts,
  entered,
  onChange,
  isMobile,
}: {
  parties: OverrideParty[];
  pcts: number[];
  entered: boolean[];
  onChange: (index: number, pct: number, hasValue: boolean) => void;
  isMobile?: boolean;
}) => {
  const { t } = useTranslation();
  const sum = pcts.reduce((total, n) => total + (n || 0), 0);
  const masterShare = 100 - sum;
  const over = masterShare < 0;
  const anyEntered = entered.some(Boolean);
  const single = parties.length === 1;

  const handleInput = (index: number) => (raw: string) => {
    if (raw.replace(/[^\d]/g, "") === "") {
      onChange(index, 0, false);
      return;
    }
    onChange(index, clampPct(raw), true);
  };

  const partyCards = parties.map((party, index) => (
    <div
      key={party.subIbId}
      className="flex flex-col gap-2 rounded-lg bg-surface-tonal-default p-4"
    >
      <label
        htmlFor={`override-split-${index}`}
        className="flex items-center gap-1.5 text-text-secondary"
      >
        <span className={`h-3.5 w-3.5 shrink-0 rounded ${party.colorClass}`} />
        <Typo element="span" type="bodyXS" textColor="text-text-secondary">
          {t("subIBReport_splitReceivesLabel", "{{name}} receives", {
            name: party.label,
          })}
        </Typo>
      </label>
      <div className="flex items-baseline justify-between gap-2">
        <input
          id={`override-split-${index}`}
          type="text"
          inputMode="numeric"
          value={entered[index] ? String(pcts[index]) : ""}
          placeholder={t("subIBReport_splitMinPlaceholder", "Min 0")}
          onChange={(e) => handleInput(index)(e.target.value)}
          className="min-w-0 flex-1 bg-transparent font-sans text-xl font-semibold leading-[1.2] text-text-primary outline-none placeholder:text-text-tertiary"
          aria-label={t(
            "subIBReport_splitReceivesAria",
            "{{name}} receives percentage",
            { name: party.label },
          )}
        />
        <Typo element="span" type="labelLG">
          %
        </Typo>
      </div>
    </div>
  ));

  const masterCard = (
    <div className="flex flex-col gap-2 rounded-lg bg-surface-brand-subtle p-4">
      <span className="flex items-center gap-1.5 text-text-brand-default">
        <span
          className={`h-3.5 w-3.5 shrink-0 rounded ${MASTER_COLOR_CLASS}`}
        />
        <Typo element="span" type="bodyXS" textColor="text-text-brand-default">
          {t("subIBReport_splitYouReceive", "You receive")}
        </Typo>
      </span>
      <div className="flex items-baseline justify-between gap-2">
        <Typo element="span" type="labelLG" textColor="text-text-brand-default">
          {anyEntered && !over ? masterShare : "--"}
        </Typo>
        <Typo element="span" type="labelLG" textColor="text-text-brand-default">
          %
        </Typo>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      <Typo element="span" type="labelSM" textColor="text-text-primary">
        {single
          ? t("subIBReport_scopedSplitSubtitle", "Rebate shared with Sub IB")
          : t(
              "subIBReport_scopedSplitSubtitlePlural",
              "Rebate shared with Sub IBs",
            )}
      </Typo>
      {/* Design change #8 spacing: 16px inside the split block, 8px between cards. */}
      <div className="flex flex-col gap-4">
        {/* Matches the default rebate split's bar (CreateProgramWizard's
            SplitStep): every Sub IB keeps a visible 4px stub at 0% so their
            colour stays readable, and `masterShare` is the only segment
            allowed to disappear — it has no stub to preserve, and an
            over-allocated split drives it negative, where a zero-width item
            would still reserve its `gap-0.5`. `min-w-[4px]` rather than a
            width floor: the percentage is the flex basis, so only a
            min-width survives the browser shrinking items to fit the gaps. */}
        <div className="flex h-4 gap-0.5 overflow-hidden rounded-full">
          {pcts.map((pct, index) => (
            <div
              key={parties[index]?.subIbId ?? index}
              className={clsx("min-w-[4px]", parties[index]?.colorClass)}
              style={{ width: `${Math.max(0, pct)}%` }}
            />
          ))}
          {masterShare > 0 && (
            <div
              className={MASTER_COLOR_CLASS}
              style={{ width: `${masterShare}%` }}
            />
          )}
        </div>

        {/* One Sub IB → the input and the derived card sit side by side.
            Two → the two inputs share a row and the derived card spans
            beneath, which is how the canvas splits `.split-cards` from
            `.split3__fields` — except on mobile, where all three stack
            full-width one per row (design QA), since two inputs plus the
            derived card don't fit side by side at that width. */}
        {isMobile ? (
          <div className="flex flex-col gap-2">
            {partyCards}
            {masterCard}
          </div>
        ) : single ? (
          <div className="grid grid-cols-2 gap-2">
            {partyCards}
            {masterCard}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2">{partyCards}</div>
            {masterCard}
          </div>
        )}

        {/* One stable live region holding either message — swapping between two
            separate regions would unmount the one the reader is listening to.
            Typo forwards no ARIA props, so the region is this wrapper. */}
        <div aria-live="polite">
          {over ? (
            <Typo element="p" type="bodySM" textColor="text-text-error">
              {t(
                "subIBReport_splitOverError",
                "Sub IB shares can't exceed 100%.",
              )}
            </Typo>
          ) : (
            <Typo element="p" type="bodySM" textColor="text-text-secondary">
              {parties.length === 1
                ? t(
                    "subIBReport_scopedSplitCaption",
                    "On a hypothetical $1,000.00 in rebate, you would keep {{dollarMaster}} and pay {{dollarSub}} to {{name}}.",
                    {
                      dollarMaster: anyEntered
                        ? fmtDollars(masterShare)
                        : "$--",
                      dollarSub: entered[0] ? fmtDollars(pcts[0]) : "$--",
                      name: parties[0]?.label,
                    },
                  )
                : t(
                    "subIBReport_scopedSplitCaptionTwoSub",
                    "On a hypothetical $1,000.00 in rebate, you would keep {{dollarMaster}} and pay {{dollarSub}} to {{name1}} and {{dollarSub2}} to {{name2}}.",
                    {
                      dollarMaster: anyEntered
                        ? fmtDollars(masterShare)
                        : "$--",
                      dollarSub: entered[0] ? fmtDollars(pcts[0]) : "$--",
                      name1: parties[0]?.label,
                      dollarSub2: entered[1] ? fmtDollars(pcts[1]) : "$--",
                      name2: parties[1]?.label,
                    },
                  )}
            </Typo>
          )}
        </div>
      </div>
    </div>
  );
};
